import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactECharts from "echarts-for-react";

import { isAdmin } from "../lib/admin";
import { useMe } from "../api/me";
import {
  HoldingOut,
  LeaderRow,
  TradeOut,
  useLeaderboard,
  usePortfolioDetail,
  useResetPortfolio,
} from "../api/portfolio";
import { echarts, PALETTE, baseAxis, baseTooltip } from "../charts/echarts";
import EmptyState from "../components/EmptyState";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import TopNav from "../components/TopNav";

function money(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function pct(v: number | null | undefined): string {
  if (v == null) return "—";
  const s = v >= 0 ? "+" : "";
  return `${s}${v.toFixed(2)}%`;
}

function pctClass(v: number | null | undefined): string {
  if (v == null) return "text-slate-400";
  return v >= 0 ? "text-emerald-400" : "text-rose-400";
}

function EquityCurve({
  nav,
}: {
  nav: { day: string; equity: number; spy: number }[];
}) {
  if (nav.length === 0)
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-sm text-slate-500">
        No NAV snapshots yet — the curve fills in once the daily mark-to-market
        runs (07:10 UTC).
      </div>
    );
  const option = {
    backgroundColor: "transparent",
    animation: false,
    tooltip: {
      ...baseTooltip,
      trigger: "axis",
      formatter: (ps: any) => {
        const day = ps[0]?.axisValue ?? "";
        const lines = ps.map(
          (p: any) =>
            `${p.marker} ${p.seriesName}: ${money(p.value)}`,
        );
        return `${day}<br/>${lines.join("<br/>")}`;
      },
    },
    legend: {
      data: ["Portfolio", "SPY $10k"],
      textStyle: { color: PALETTE.text, fontSize: 11 },
      top: 0,
    },
    grid: { left: 52, right: 16, top: 28, bottom: 28 },
    xAxis: {
      ...baseAxis,
      type: "category",
      data: nav.map((n) => n.day),
    },
    yAxis: {
      ...baseAxis,
      type: "value",
      scale: true,
      axisLabel: { ...baseAxis.axisLabel, formatter: (v: number) => `$${(v / 1000).toFixed(1)}k` },
    },
    series: [
      {
        name: "Portfolio",
        type: "line",
        showSymbol: false,
        smooth: false,
        lineStyle: { width: 2, color: PALETTE.bull },
        data: nav.map((n) => n.equity),
      },
      {
        name: "SPY $10k",
        type: "line",
        showSymbol: false,
        smooth: false,
        lineStyle: { width: 1.5, color: PALETTE.text, type: "dashed" },
        data: nav.map((n) => n.spy),
      },
    ],
  } satisfies echarts.EChartsCoreOption;
  return (
    <ReactECharts option={option} style={{ height: 260, width: "100%" }} echarts={echarts} notMerge />
  );
}

function StatCard({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-2xl tabular-nums ${cls ?? "text-slate-100"}`}>{value}</div>
    </div>
  );
}

function HoldingsTable({ holdings }: { holdings: HoldingOut[] }) {
  if (holdings.length === 0)
    return <div className="text-sm text-slate-500">In cash — no open positions.</div>;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">Horizon</th>
            <th className="px-3 py-2 text-right">Shares</th>
            <th className="px-3 py-2 text-right">Avg cost</th>
            <th className="px-3 py-2 text-right">Mark</th>
            <th className="px-3 py-2 text-right">Value</th>
            <th className="px-3 py-2 text-right">Unreal.</th>
            <th className="px-3 py-2 text-right">Weight</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {holdings.map((h) => (
            <tr key={h.ticker} className="bg-slate-950">
              <td className="px-3 py-2">
                <Link to={`/s/${encodeURIComponent(h.yahoo_symbol)}`} className="font-mono text-slate-100 hover:text-sky-300">
                  {h.shares < 0 ? `${h.ticker} (short)` : h.ticker}
                </Link>
              </td>
              <td className="px-3 py-2 text-slate-400">{h.horizon}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{h.shares.toFixed(2)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">${h.avg_cost.toFixed(2)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{h.mark != null ? `$${h.mark.toFixed(2)}` : "—"}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{money(h.market_value)}</td>
              <td className={`px-3 py-2 text-right font-mono tabular-nums ${pctClass(h.unrealized)}`}>{h.unrealized != null ? money(h.unrealized) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{h.weight != null ? `${(h.weight * 100).toFixed(0)}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Blotter({ trades }: { trades: TradeOut[] }) {
  if (trades.length === 0)
    return <div className="text-sm text-slate-500">No trades yet.</div>;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">Side</th>
            <th className="px-3 py-2 text-right">Shares</th>
            <th className="px-3 py-2 text-right">Price</th>
            <th className="px-3 py-2 text-right">Gross</th>
            <th className="px-3 py-2">Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {trades.map((t, i) => {
            const buy = t.side === "buy" || t.side === "cover";
            return (
              <tr key={i} className="bg-slate-950">
                <td className="px-3 py-2 text-[11px] text-slate-400">{new Date(t.ts).toLocaleDateString()}</td>
                <td className="px-3 py-2 font-mono text-slate-200">{t.ticker}</td>
                <td className={`px-3 py-2 font-medium ${buy ? "text-emerald-400" : "text-rose-400"}`}>{t.side}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{t.shares.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">${t.price.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{money(t.gross)}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{t.reason}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Detail({ persona }: { persona: string }) {
  const { data, isLoading } = usePortfolioDetail(persona);
  if (isLoading || !data) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">{data.display_name}</h2>
        {data.tagline && <p className="text-[12px] text-slate-500">{data.tagline}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Equity" value={money(data.equity)} />
        <StatCard label="Return" value={pct(data.return_pct)} cls={pctClass(data.return_pct)} />
        <StatCard label="vs SPY" value={pct(data.excess_pct)} cls={pctClass(data.excess_pct)} />
        <StatCard label="Cash" value={money(data.cash)} />
      </div>

      <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
        <header className="mb-2 text-sm font-medium text-slate-300">Equity vs SPY</header>
        <EquityCurve nav={data.nav} />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-slate-300">Holdings</h3>
        <HoldingsTable holdings={data.holdings} />
      </section>

      {data.attribution.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-slate-300">By horizon</h3>
          <div className="flex flex-wrap gap-2">
            {data.attribution.map((a) => (
              <div key={a.horizon} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-[12px]">
                <span className="text-slate-400">{a.horizon}</span>{" "}
                <span className="font-mono text-slate-200">{money(a.market_value)}</span>{" "}
                <span className={`font-mono ${pctClass(a.unrealized)}`}>({money(a.unrealized)})</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-slate-300">Recent trades</h3>
        <Blotter trades={data.trades} />
      </section>
    </div>
  );
}

function LeaderboardRow({
  row,
  selected,
  onSelect,
}: {
  row: LeaderRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition ${
        selected ? "border-sky-500/50 bg-slate-900" : "border-slate-800 bg-slate-950 hover:border-slate-700"
      } ${row.is_benchmark ? "opacity-80" : ""}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-100">{row.display_name}</span>
          {row.is_benchmark && (
            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">benchmark</span>
          )}
        </div>
        <div className="truncate text-[11px] text-slate-500">{row.tagline}</div>
      </div>
      <div className="flex shrink-0 items-center gap-4 text-right">
        <div>
          <div className="font-mono text-sm tabular-nums text-slate-100">{money(row.equity)}</div>
          <div className={`text-[11px] tabular-nums ${pctClass(row.return_pct)}`}>{pct(row.return_pct)}</div>
        </div>
        {!row.is_benchmark && (
          <div className="w-20">
            <div className={`font-mono text-sm tabular-nums ${pctClass(row.excess_pct)}`}>{pct(row.excess_pct)}</div>
            <div className="text-[10px] text-slate-500">vs SPY</div>
          </div>
        )}
      </div>
    </button>
  );
}

export default function Portfolio() {
  const me = useMe();
  const showAdmin = isAdmin(me.data?.email);
  const { data: rows, isLoading } = useLeaderboard();
  const reset = useResetPortfolio();
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!selected && rows && rows.length > 0) {
      const firstActive = rows.find((r) => !r.is_benchmark) ?? rows[0];
      setSelected(firstActive.persona);
    }
  }, [rows, selected]);

  const season = rows?.[0]?.season;
  const since = rows?.[0]?.since ? new Date(rows[0].since).toLocaleDateString() : null;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Paper portfolios</h1>
          <p className="text-[12px] text-slate-500">
            {season ? `Season ${season}` : ""}{since ? ` · since ${since}` : ""} · $10k each, scored vs SPY buy-and-hold
          </p>
        </div>
        {showAdmin && (
          <button
            onClick={() => {
              if (window.confirm("Reset all personas to a fresh $10k season? Past seasons stay queryable.")) {
                reset.mutate(undefined, { onSuccess: () => setSelected(null) });
              }
            }}
            disabled={reset.isPending}
            className="rounded-full border border-slate-800 px-3 py-1 text-[11px] uppercase tracking-wide text-slate-400 transition hover:border-rose-500/40 hover:text-rose-300 disabled:opacity-50"
          >
            {reset.isPending ? "Resetting…" : "Reset season"}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !rows || rows.length === 0 ? (
        <EmptyState title="No portfolios yet" hint="The persona portfolios seed on the next deploy and start trading at the 07:00 UTC tick." />
      ) : (
        <div className="space-y-6">
          <div className="space-y-2">
            {rows.map((r) => (
              <LeaderboardRow
                key={r.persona}
                row={r}
                selected={selected === r.persona}
                onSelect={() => setSelected(r.persona)}
              />
            ))}
          </div>

          {selected && (
            <div className="border-t border-slate-800 pt-6">
              <Detail persona={selected} />
            </div>
          )}

          <p className="text-[11px] leading-relaxed text-slate-500">
            Paper trading only. Fills assume the latest daily close plus a small
            slippage + IBKR-style commission, and ignore short-borrow cost and
            real-world market impact — so results are an optimistic upper bound.
            The honest yardstick is the “vs SPY” column, not the raw equity.
          </p>
        </div>
      )}

      <Footer note="Paper portfolios tick daily at 07:00 UTC · marked to market at 07:10 UTC" />
    </div>
  );
}
